import { useEffect, useState } from "react";
import { portalApi } from "./portalApi";
import { PortalHandouts } from "./PortalHandouts";
import { Modal, Empty } from "../components/UI";
import { PlayCircle, CheckCircle2, GraduationCap, FileText, Newspaper, Rss, ScrollText, ExternalLink, HeartPulse } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const KIND_ICONS = { Article: Newspaper, Blog: Rss, Whitepaper: ScrollText, Document: FileText };
const absUrl = (u) => (u?.startsWith("http") ? u : `${API_BASE}${u}`);

export default function PortalEducation() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("videos");
  const [watching, setWatching] = useState(null);

  const load = () => portalApi.get("/portal/education").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openVideo = async (r) => {
    setWatching(r);
    try { await portalApi.post(`/portal/education/${r.id}/view`); load(); } catch { /* view log best-effort */ }
  };
  const openDoc = async (r) => {
    try { await portalApi.post(`/portal/education/${r.id}/view`); } catch { /* view log best-effort */ }
    window.open(absUrl(r.url), "_blank", "noopener");
    load();
  };

  const videos = (rows || []).filter((r) => (r.resource_type || "video") !== "document");
  const docs = (rows || []).filter((r) => r.resource_type === "document");
  const shown = tab === "videos" ? videos : docs;
  const cats = [...new Set(shown.map((r) => r.category))];

  return (
    <div className="space-y-5" data-testid="portal-education-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-[#3E2723]"><GraduationCap size={20} className="text-[#7B6157]" /> Education & Wellness</h1>
        <p className="text-sm text-[#7B6157]">Videos, articles, and documents hand-picked by your care team to support your recovery journey.</p>
      </div>

      <div data-testid="portal-edu-disclaimer" className="flex items-start gap-2.5 rounded-xl border border-[#FFE082] bg-[#FFF8E1] px-4 py-3 text-xs leading-relaxed text-[#6D4C41]">
        <HeartPulse size={15} className="mt-0.5 shrink-0 text-[#8D5A00]" />
        <span>These resources are for education only. They are not medical advice, and they do not replace guidance from your care team. If you are in crisis, call or text 988 (Suicide & Crisis Lifeline) or call 911.</span>
      </div>

      <PortalHandouts />

      <div className="flex gap-2">
        {[{ k: "videos", label: `Videos (${videos.length})` }, { k: "documents", label: `Documents & Articles (${docs.length})` }].map(({ k, label }) => (
          <button key={k} data-testid={`portal-edu-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${tab === k ? "bg-[#4E342E] text-white shadow" : "bg-white border border-[#E5DFD3] text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>
            {label}
          </button>
        ))}
      </div>

      {rows === null ? (
        <div className="animate-pulse space-y-3"><div className="h-6 w-40 rounded bg-[#EFEBE9]" /><div className="h-32 rounded-xl bg-[#EFEBE9]" /></div>
      ) : shown.length === 0 ? (
        <Empty text={tab === "videos" ? "Your clinic hasn't published any videos yet" : "Your clinic hasn't published any documents or articles yet"} />
      ) : cats.map((cat) => (
        <div key={cat}>
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">{cat}</h3>
          {tab === "videos" ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {videos.filter((r) => r.category === cat).map((r) => (
                <button key={r.id} data-testid={`portal-edu-${r.id}`} onClick={() => openVideo(r)}
                  className="overflow-hidden rounded-2xl bg-white border border-[#EFEBE0] text-left hover:shadow-md transition-shadow">
                  <div className="relative">
                    {r.video_type === "youtube" && r.youtube_id ? (
                      <img src={`https://img.youtube.com/vi/${r.youtube_id}/mqdefault.jpg`} alt="" className="h-36 w-full object-cover" />
                    ) : (
                      <div className="flex h-36 w-full items-center justify-center bg-[#3E2723]"><PlayCircle size={38} className="text-[#FFB300]" /></div>
                    )}
                    {r.watched && (
                      <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-[#2E7D32] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`portal-edu-watched-${r.id}`}>
                        <CheckCircle2 size={10} /> Watched
                      </span>
                    )}
                  </div>
                  <div className="p-3.5">
                    <div className="font-heading text-sm font-bold text-[#3E2723]">{r.title}</div>
                    <p className="mt-0.5 text-xs text-[#7B6157] line-clamp-2">{r.description}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {docs.filter((r) => r.category === cat).map((r) => {
                const KindIcon = KIND_ICONS[r.doc_kind] || FileText;
                return (
                  <button key={r.id} data-testid={`portal-edu-${r.id}`} onClick={() => openDoc(r)}
                    className="flex items-start gap-3 rounded-2xl bg-white border border-[#EFEBE0] p-4 text-left hover:shadow-md transition-shadow">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F5F2EB] text-[#7B6157]"><KindIcon size={18} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="rounded-full bg-[#EDE7F6] px-2 py-0.5 text-[10px] font-bold text-[#5E35B1]">{r.doc_kind || "Document"}</span>
                        {r.watched && (
                          <span className="flex items-center gap-1 rounded-full bg-[#2E7D32] px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`portal-edu-watched-${r.id}`}>
                            <CheckCircle2 size={10} /> Viewed
                          </span>
                        )}
                        <ExternalLink size={12} className="ml-auto shrink-0 text-[#84675B]" />
                      </span>
                      <span className="mt-1 block font-heading text-sm font-bold text-[#3E2723]">{r.title}</span>
                      <span className="mt-0.5 block text-xs text-[#7B6157] line-clamp-2">{r.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}

      <Modal open={!!watching} onClose={() => setWatching(null)} title={watching?.title || ""} wide>
        {watching && (
          <div className="space-y-3" data-testid="portal-edu-player">
            {watching.video_type === "youtube" && watching.youtube_id ? (
              <div className="aspect-video w-full overflow-hidden rounded-xl">
                <iframe title={watching.title} src={`https://www.youtube.com/embed/${watching.youtube_id}?rel=0`}
                  className="h-full w-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <video controls className="w-full rounded-xl bg-black max-h-[420px]" src={absUrl(watching.url)} />
            )}
            {watching.description && <p className="text-sm text-[#5D4037]">{watching.description}</p>}
            <p className="text-[10px] text-[#84675B]">Education only - not medical advice. Talk to your care team about anything you watch here.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
